class TkProductMediaGallery extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.io = null;
    this.variantChangeUnsubscriber = null;
    this.onKey = this.onKey.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
  }

  connectedCallback() {
    this.track = this.querySelector('.tk-product-media-gallery__track');
    this.slides = Array.from(this.querySelectorAll('.tk-product-media-gallery__slide'));
    this.thumbs = Array.from(this.querySelectorAll('.tk-product-media-gallery__thumb'));
    this.navPrev = this.querySelector('.tk-product-media-gallery__nav--prev');
    this.navNext = this.querySelector('.tk-product-media-gallery__nav--next');
    this.live = this.querySelector('.tk-product-media-gallery__live');
    this.hideVariants = this.dataset.hideVariants === 'true';

    if (!this.track || this.slides.length === 0) return;

    this.bindEvents();
    this.observeSlides();
    this.scrollToStart();
  }

  scrollToStart() {
    const startIndex = parseInt(this.dataset.startIndex, 10);
    if (!startIndex || startIndex === 0) return;
    this.currentIndex = startIndex;
    const slide = this.slides[startIndex];
    if (!slide) return;
    requestAnimationFrame(() => {
      this.track.scrollLeft = slide.offsetLeft - this.track.offsetLeft;
    });
  }

  disconnectedCallback() {
    if (this.io) {
      this.io.disconnect();
      this.io = null;
    }
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
      this.variantChangeUnsubscriber = null;
    }
    this.removeEventListener('keydown', this.onKey);
  }

  bindEvents() {
    if (this.navPrev) this.navPrev.addEventListener('click', () => this.go(-1));
    if (this.navNext) this.navNext.addEventListener('click', () => this.go(1));
    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        this.goTo(parseInt(thumb.dataset.index, 10));
      });
    });
    this.addEventListener('keydown', this.onKey);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        this.onVariantChange
      );
    }
  }

  getVisibleSlides() {
    return this.slides.filter((s) => !s.classList.contains('tk-product-media-gallery__slide--hidden'));
  }

  go(direction) {
    const visible = this.getVisibleSlides();
    const total = visible.length;
    if (total === 0) return;
    let currentVisibleIdx = visible.findIndex(
      (s) => parseInt(s.dataset.index, 10) === this.currentIndex
    );
    if (currentVisibleIdx === -1) currentVisibleIdx = 0;
    const nextVisibleIdx = (currentVisibleIdx + direction + total) % total;
    const nextSlide = visible[nextVisibleIdx];
    this.goTo(parseInt(nextSlide.dataset.index, 10));
  }

  goTo(index) {
    const slide = this.slides[index];
    if (!slide) return;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Scroll del solo track: scrollIntoView sposterebbe anche la pagina
    // (block: nearest) quando il cambio variante parte dallo sticky ATC.
    this.track.scrollTo({
      left: slide.offsetLeft - this.track.offsetLeft,
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  }

  observeSlides() {
    this.io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const index = parseInt(entry.target.dataset.index, 10);
            this.setActive(index);
          }
        });
      },
      { root: this.track, threshold: 0.5 }
    );
    this.slides.forEach((slide) => this.io.observe(slide));
  }

  setActive(index) {
    if (index === this.currentIndex) return;
    this.currentIndex = index;
    let activeThumb = null;
    this.thumbs.forEach((thumb) => {
      const thumbIdx = parseInt(thumb.dataset.index, 10);
      const isActive = thumbIdx === index;
      thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
      if (isActive) activeThumb = thumb;
    });
    this.slides.forEach((slide) => {
      const slideIdx = parseInt(slide.dataset.index, 10);
      slide.classList.toggle('is-active', slideIdx === index);
    });
    if (activeThumb) {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const container = activeThumb.parentElement;
      const left = activeThumb.offsetLeft - container.offsetLeft;
      const right = left + activeThumb.offsetWidth;
      if (left < container.scrollLeft) {
        container.scrollTo({ left: left, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
      } else if (right > container.scrollLeft + container.clientWidth) {
        container.scrollTo({
          left: right - container.clientWidth,
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
        });
      }
    }
    if (this.live) {
      const visible = this.getVisibleSlides();
      const visibleCurrentIdx = visible.findIndex((s) => parseInt(s.dataset.index, 10) === index);
      this.live.textContent = `${visibleCurrentIdx + 1} / ${visible.length}`;
    }
  }

  onKey(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.go(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.go(1);
    }
  }

  onVariantChange(payload) {
    const variant = payload?.data?.variant;
    const mediaId = variant?.featured_media?.id;
    if (!mediaId) return;

    if (this.hideVariants) {
      this.updateHiddenState(mediaId);
    }

    const slide = this.slides.find((s) => String(s.dataset.mediaId) === String(mediaId));
    if (!slide) return;
    const targetIndex = parseInt(slide.dataset.index, 10);
    if (targetIndex === this.currentIndex) return;
    this.goTo(targetIndex);
  }

  updateHiddenState(activeMediaId) {
    const activeIdStr = String(activeMediaId);
    this.slides.forEach((slide) => {
      const isVariantImage = slide.hasAttribute('data-variant-image');
      const isActiveMedia = String(slide.dataset.mediaId) === activeIdStr;
      slide.classList.toggle('tk-product-media-gallery__slide--hidden', isVariantImage && !isActiveMedia);
    });
    this.thumbs.forEach((thumb, i) => {
      const isVariantImage = thumb.hasAttribute('data-variant-image');
      const matchingSlide = this.slides[i];
      const isActiveMedia = matchingSlide && String(matchingSlide.dataset.mediaId) === activeIdStr;
      thumb.classList.toggle('tk-product-media-gallery__thumb--hidden', isVariantImage && !isActiveMedia);
    });
  }
}

if (!customElements.get('tk-product-media-gallery')) {
  customElements.define('tk-product-media-gallery', TkProductMediaGallery);
}
